import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { UserMinus, UserPlus, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';

export const WrPermissions = ({ t }) => {
  const [data, setData] = useState({ editors: [], candidates: [] });
  const [pick, setPick] = useState('');

  const load = async () => {
    const r = await api.get('/wr/editors');
    setData(r.data);
  };
  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, []); // eslint-disable-line

  const grant = async () => {
    try {
      await api.post('/wr/editors', { user_id: pick });
      toast.success(t('wr.perms.granted'));
      setPick(''); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const revoke = async (uid) => {
    try {
      await api.delete(`/wr/editors/${uid}`);
      toast.success(t('wr.perms.revoked'));
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2 items-start" data-testid="wr-permissions">
      <Card className="p-6 lg:p-8 space-y-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">{t('wr.perms.title')}</h3>
            <p className="text-base text-muted-foreground mt-1 leading-relaxed">{t('wr.perms.hint')}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger className="text-base" data-testid="wr-perm-candidate-select">
              <SelectValue placeholder={t('wr.perms.pick')} />
            </SelectTrigger>
            <SelectContent>
              {data.candidates.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name} {c.position ? `— ${c.position}` : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button className="rounded-full gap-1.5 shrink-0" disabled={!pick} onClick={grant} data-testid="wr-perm-grant-btn">
            <UserPlus className="h-4 w-4" /> {t('wr.perms.grant')}
          </Button>
        </div>
      </Card>
      <Card className="p-6 lg:p-8 space-y-3" data-testid="wr-editors-list">
        <h3 className="text-lg font-semibold">{t('wr.perms.editorsTitle')} ({data.editors.length})</h3>
        {data.editors.length === 0 && <p className="text-base text-muted-foreground">{t('wr.perms.empty')}</p>}
        {data.editors.map((e) => (
          <div key={e.id} className="flex items-center gap-3 rounded-xl border px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="text-base font-medium">{e.name}</p>
              <p className="text-sm text-muted-foreground truncate">{[e.position, e.email].filter(Boolean).join(' · ')}</p>
            </div>
            <Button variant="outline" size="sm" className="gap-1.5 rounded-full" onClick={() => revoke(e.id)} data-testid={`wr-perm-revoke-${e.id}`}>
              <UserMinus className="h-4 w-4" /> {t('wr.perms.revoke')}
            </Button>
          </div>
        ))}
      </Card>
    </div>
  );
};
